// ComediantesConfig.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaPlus, FaChevronLeft } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { getComediantesPaginados, createComediante, updateComediante, deleteComediante, toggleComediante  } from '../../../components/http/Actions/ComediantesAction';

// Components
import ComedianteTable from './components/ComedianteTable';
import CreateComedianteModal from './components/CreateComedianteModal';
import EditComedianteModal from './components/EditComedianteModal';
import ToggleStatusModal from './components/ToggleStatusModal';
import Notification from '../../../components/Notification';
import Pagination from '../../../components/Pagination';
import SearchBar from '../../../components/SearchBar';

const ComediantesConfig = () => {
  // Estado para almacenar la lista de comediantes
  const [comediantes, setComediantes] = useState([]);
  
  // Estado para búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Estado para paginación
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 5,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });
  
  // Estados para modales
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [comedianteToEdit, setComedianteToEdit] = useState(null);
  const [comedianteToToggle, setComedianteToToggle] = useState(null);
  
  // Estado para nuevo comediante
  const [newComediante, setNewComediante] = useState({
    nombre: '',
    biografia: '',
    imagen: '',
    applicationUserId: '' // Agregar esta línea
  });

  
  // token
  const token = localStorage.getItem("token");
  // Estado para errores y notificaciones
  const [error, setError] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);

  // Efecto para debounce en la búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Función para mostrar notificación
  const showNotification = useCallback((message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  }, []);

  // Función para cargar comediantes con paginación
  const fetchComediantes = useCallback(async (page = 1, pageSize = 5, search = '') => {
    setIsLoading(true);
    try {
      await getComediantesPaginados(
        (data) => {
          if (data && data.data) {
            setComediantes(data.data.data);
            setPagination({
              currentPage: data.data.currentPage,
              pageSize: data.data.pageSize,
              totalCount: data.data.totalCount,
              totalPages: data.data.totalPages,
              hasNext: data.data.hasNext,
              hasPrevious: data.data.hasPrevious
            });
          } else {
            console.error("La respuesta de la API no tiene el formato esperado:", data);
            setComediantes([]);
          }
        },
        setError,
        page,
        pageSize,
        search,
        token
      );
    } catch (error) {
      console.error("Error fetching comediantes:", error);
      showNotification("Error al cargar los comediantes", "error");
    } finally {
      setIsLoading(false);
    }
  }, [showNotification, token]);

  // Función para cambiar de página
  const handlePageChange = (newPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: newPage
    }));
  };
  
  // Función para buscar
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setPagination(prev => ({
      ...prev,
      currentPage: 1
    }));
  };

  // Cargar comediantes cuando cambia la página o el término de búsqueda
  useEffect(() => {
    fetchComediantes(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
  }, [pagination.currentPage, debouncedSearchTerm, fetchComediantes, pagination.pageSize]);

  // Función para abrir modal de edición
  const openEditModal = (comediante) => {
    setComedianteToEdit({
      ...comediante,
      comedianteId: comediante.comedianteId || comediante.id || comediante.comedianteID
    });
    setShowEditModal(true);
  };


  // Función para abrir modal de creación
  const openCreateModal = () => {
    setNewComediante({
      nombre: '',
      biografia: '',
      imagen: '',
      applicationUserId: '' 
    });
    setShowCreateModal(true);
  };

  // Crear comediante
const handleCreateSubmit = async (e, newComedianteData) => {
  e.preventDefault();
  setIsLoading(true);

  try {
    const formData = new FormData();
    formData.append("nombre", newComedianteData.nombre);
    formData.append("biografia", newComedianteData.biografia);
    formData.append("applicationUserId", newComedianteData.applicationUserId || "");

    // Si hay archivo, se manda; si no, se manda URL (si existe)
    if (newComedianteData.imagenFile) {
      formData.append("imagenFile", newComedianteData.imagenFile);
    } else if (newComedianteData.imagen) {
      formData.append("imagen", newComedianteData.imagen);
    }

    await createComediante(formData, setError, token);
    fetchComediantes(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
    showNotification(`Comediante "${newComedianteData.nombre}" creado con éxito`, "success");
    setShowCreateModal(false);
  } catch (error) {
    console.error("Error creating comediante:", error);
  } finally {
    setIsLoading(false);
  }
};

// Editar comediante
const handleEditSubmit = async (e, editedComediante) => {
  e.preventDefault();
  setIsLoading(true);

  try {
    const formData = new FormData();
    formData.append("nombre", editedComediante.nombre);
    formData.append("biografia", editedComediante.biografia);
    formData.append("applicationUserId", editedComediante.applicationUserId || "");

    if (editedComediante.imagenFile) {
      formData.append("imagenFile", editedComediante.imagenFile);
    } else if (editedComediante.imagen) {
      formData.append("imagen", editedComediante.imagen);
    }

    await updateComediante(editedComediante.comedianteId, formData, setError, token);
    fetchComediantes(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
    showNotification(`Comediante "${editedComediante.nombre}" actualizado con éxito`, "success");
    setShowEditModal(false);
  } catch (error) {
    console.error("Error updating comediante:", error);
  } finally {
    setIsLoading(false);
  }
};


  // Función para eliminar comediante
  const handleDeleteComediante = async (comedianteId, nombre) => {
    if (window.confirm(`¿Estás seguro de eliminar al comediante "${nombre}"?`)) {
      setIsLoading(true);
      
      try {
        await deleteComediante(comedianteId, setError, token);
        fetchComediantes(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
        showNotification(`Comediante "${nombre}" eliminado con éxito`, "success");
      } catch (error) {
        console.error("Error deleting comediante:", error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Función para abrir el modal de cambio de estado
  const handleToggleStatusClick = (comediante) => {
    setComedianteToToggle(comediante);
    setShowToggleModal(true);
  };

  // Función para cambiar estado del comediante (habilitar/deshabilitar)
const handleToggleStatus = async (comediante) => {
  setIsLoading(true);
  
  try {
    const nuevoEstado = !comediante.habilitado;

    // Llamar al nuevo endpoint PATCH
    await toggleComediante(comediante.comedianteId, nuevoEstado, setError, token);

    // Actualizar tabla y notificar
    fetchComediantes(pagination.currentPage, pagination.pageSize, debouncedSearchTerm);
    showNotification(
      `Comediante "${comediante.nombre}" ${nuevoEstado ? "habilitado" : "deshabilitado"} con éxito`,
      "success"
    );
    setShowToggleModal(false);
  } catch (error) {
    console.error(`Error al cambiar estado del comediante:`, error);
    showNotification("No se pudo cambiar el estado del comediante", "error");
  } finally {
    setIsLoading(false);
  }
};



  return (
    <div className="min-h-screen bg-zinc-900">
      {/* Header */}
      <header className="bg-zinc-800 shadow-lg">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/admin" className="mr-4">
              <FaChevronLeft className="text-white hover:text-red-500 transition-colors" />
            </Link>
            <h1 className="text-3xl font-bold text-white">Gestor de Comediantes</h1>
          </div>
          <button 
            onClick={openCreateModal}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Nuevo Comediante
          </button>
        </div>
      </header>

      {/* Notification */}
      {notification.show && (
        <Notification message={notification.message} type={notification.type} />
      )}

      {/* Main content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Search */}
        <SearchBar 
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          placeholder="Buscar por nombre o biografía..."
        />

        {/* Loading state */}
        {isLoading && (
          <div className="flex justify-center my-12">
            <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-red-600 rounded-full animate-spin"></div>
          </div>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <div className="bg-red-500/20 border border-red-500 text-red-100 px-6 py-4 rounded-lg mb-6">
            <p>{error}</p>
          </div>
        )}

        {/* Comediantes table */}
        {!isLoading && !error && (
          <ComedianteTable 
            comediantes={comediantes} 
            onEdit={openEditModal} 
            onToggleStatus={handleToggleStatusClick} 
          />
        )}
        
        {/* Paginación */}
        {!isLoading && !error && pagination.totalCount > 0 && (
          <Pagination 
            pagination={pagination} 
            handlePageChange={handlePageChange} 
          />
        )}
      </main>

      {/* Edit modal */}
      {showEditModal && comedianteToEdit && (
        <EditComedianteModal 
          comedianteToEdit={comedianteToEdit}
          setComedianteToEdit={setComedianteToEdit}
          handleEditSubmit={handleEditSubmit}
          isLoading={isLoading}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Create modal */}
      {showCreateModal && (
        <CreateComedianteModal 
          newComediante={newComediante}
          setNewComediante={setNewComediante}
          handleCreateSubmit={handleCreateSubmit}
          isLoading={isLoading}
          onClose={() => setShowCreateModal(false)}
        />
      )}
      
      {/* Toggle Status modal */}
      {showToggleModal && comedianteToToggle && (
        <ToggleStatusModal 
          comediante={comedianteToToggle}
          onConfirm={handleToggleStatus}
          onClose={() => setShowToggleModal(false)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default ComediantesConfig;